import { useState, useCallback } from 'react';

import { ButtonColors } from 'types';
import { Dialog } from 'components/dialog';

/**
 * Owner 2026-10-16: one Russ invoice is billed once. Main refuses a save that
 * sets an LTL Invoice # already on another stock order or transfer (409,
 * CONFIRM_SHARED_LTL_INVOICE) and saves it when the same save is sent again
 * with `confirmSharedLtlInvoice: true` (one invoice for several loads). This
 * asks the user before resending.
 */
export const SHARED_LTL_INVOICE_ACTION = 'CONFIRM_SHARED_LTL_INVOICE';

type ApiError = {
  status?: number;
  data?: { code?: string; error?: string; action?: string; message?: string };
};

/** Main's message for a shared-invoice refusal, or null for any other error. */
export const sharedLtlInvoiceMessage = (error: unknown): string | null => {
  const { data, status } = (error || {}) as ApiError;
  if (
    status !== 409 ||
    !data ||
    (data.action !== SHARED_LTL_INVOICE_ACTION &&
      data.code !== SHARED_LTL_INVOICE_ACTION)
  ) {
    return null;
  }
  return (
    data.error ||
    data.message ||
    'This LTL Invoice # is already on another stock record.'
  );
};

export const useSharedLtlInvoiceConfirm = () => {
  const [pending, setPending] = useState<{
    message: string;
    retry: () => void;
  } | null>(null);

  /** True (and the question is asked) when `error` is the shared refusal. */
  const ask = useCallback((error: unknown, retry: () => void) => {
    const message = sharedLtlInvoiceMessage(error);
    if (!message) {
      return false;
    }
    setPending({ retry, message });
    return true;
  }, []);

  const dialog = pending ? (
    <Dialog
      open
      width="480px"
      text={pending.message}
      confirmText="Save anyway"
      confirmStyle={ButtonColors.Blue}
      onClose={() => setPending(null)}
      title="LTL Invoice # already used"
      onConfirm={() => {
        const { retry } = pending;
        setPending(null);
        retry();
      }}
    />
  ) : null;

  return { ask, dialog };
};
